import {PromptPool, VisualizationPromptPoolType} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";
import {StandardizeTextForTTS} from "../../../../Utils/Services/TTS.js";

/** Convert a prompt pool target to the final TTS text that will be spoken/baked.
 * This includes:
 * 1. Standardizing the text (digit conversion, etc.)
 * 2. Adding the TTS prefix */
export function PromptPoolTargetToTTSText(target: string, pool: PromptPool): string {
	// First standardize the target text
	const standardizedTarget = StandardizeTextForTTS(target, pool.ttsConvertDigitsToWords ?? false, true);

	// Then apply TTS prefix if set
	const finalText = pool.ttsPrefix ? `${pool.ttsPrefix} ${standardizedTarget}` : standardizedTarget;

	return finalText;
}

/** Build the text for the last subprompt able to be heard: replace the "$sceneLetter" and "$sceneEntityName"
 * placeholders in the pool's suffix with the target scene's letter / anchor-entity name, then append the result.
 * Pure complement to PromptPoolTargetToTTSText. */
export function AddSuffixToPromptPoolTargetTTSText(textToRepeat: string, pool: PromptPool, sceneInfo: TargetSceneInfo|n): string {
	let suffix = pool.ttsSuffix_forLastSubprompt ?? "";
	// substitute the "$sceneLetter" placeholder with the target scene's single uppercase letter (if any)
	if (suffix.includes("$sceneLetter") && sceneInfo?.letter) {
		suffix = suffix.replaceAll("$sceneLetter", sceneInfo.letter);
	}
	// substitute the "$sceneEntityName" placeholder with the target scene's anchor-entity name (if any)
	if (suffix.includes("$sceneEntityName") && sceneInfo?.entityName) {
		suffix = suffix.replaceAll("$sceneEntityName", sceneInfo.entityName);
	}
	return suffix ? `${textToRepeat} ${suffix}` : textToRepeat;
}

/**
 * Calculate distances from a starting room to all other rooms in the pool.
 * Uses Dijkstra's algorithm to find the shortest path by distance (not by jump count).
 * Returns a Map with:
 * - key: room name
 * - value: {distance: total distance (sum of edge weights), jumps: number of edges traversed}
 */
export function GetDistancesFromRoomToAllOthers(roomPool: Room[], startRoomName: string|n): Map<string, {distance: number, jumps: number}> {
	const result = new Map<string, {distance: number, jumps: number}>();

	// handle special case: no starting room (first prompt)
	if (startRoomName == null) {
		// all rooms are at distance 0 with 0 jumps
		for (const room of roomPool) {
			result.set(room.name, {distance: 0, jumps: 0});
		}
		return result;
	}

	// build a graph of connections between rooms
	const connections = new Map<string, Array<{to: string, distance: number}>>();
	for (const room of roomPool) {
		// add bidirectional connections based on linksToHere
		for (const link of room.linksToHere) {
			// connection from link.from to room.name
			if (!connections.has(link.from)) {
				connections.set(link.from, []);
			}
			connections.get(link.from)!.push({to: room.name, distance: link.distance});

			// connection from room.name to link.from (bidirectional)
			if (!connections.has(room.name)) {
				connections.set(room.name, []);
			}
			connections.get(room.name)!.push({to: link.from, distance: link.distance});
		}
	}

	// Dijkstra's algorithm to find shortest paths by distance
	// Priority queue implemented as array, sorted by distance (for simplicity; could use a proper heap for larger graphs)
	const queue: Array<{room: string, totalDistance: number, jumps: number}> = [{room: startRoomName, totalDistance: 0, jumps: 0}];

	while (queue.length > 0) {
		// sort queue by distance to get the node with minimum distance (Dijkstra's key operation)
		queue.sort((a, b)=>a.totalDistance - b.totalDistance);
		const current = queue.shift()!;

		// skip if already processed (we already found a shorter path to this room)
		if (result.has(current.room)) continue;

		// record this room's distance and jumps
		result.set(current.room, {distance: current.totalDistance, jumps: current.jumps});

		// explore neighbors
		const neighbors = connections.get(current.room) || [];
		for (const neighbor of neighbors) {
			if (!result.has(neighbor.to)) {
				queue.push({
					room: neighbor.to,
					totalDistance: current.totalDistance + neighbor.distance,
					jumps: current.jumps + 1,
				});
			}
		}
	}

	return result;
}

export type TargetSceneInfo = {
	letter: string; // the target scene's grid-cell letter (lowercase)
	entityName: string; // the target scene's anchor-entity name
};

/** A single visualization-prompt target. `sceneInfo` is populated for grid-scene targets (used to
 * substitute the $sceneLetter / $sceneEntityName placeholders in last-subprompt suffixes), else null. */
export type Target = {
	text: string;
	sceneInfo?: TargetSceneInfo|n;
};

/** Create a Target. (sceneInfo defaults to null, for target-types that don't carry grid-scene data) */
export const MakeTarget = (text: string, sceneInfo: TargetSceneInfo|n = null): Target=>({text, sceneInfo});

// oxlint-disable-next-line lf/sc
/** Example...
Input:
```
Your: shelves, bed, [closet]
[^->2]Kitchen: microwave, sink, fridge
[^->4]Garage: door, toolbox, heater
[^->2][Kitchen->4]HallCloset
```
Output(rooms): [
	{name: "Your", linksToHere: [], targets: [{text: "Your shelves"}, {text: "Your bed"}, {text: "Your closet"}]},
	{name: "Kitchen", linksToHere: [{from: "Your", distance: 2}], targets: [{text: "Kitchen microwave"}, {text: "Kitchen sink"}, {text: "Kitchen fridge"}]},
	{name: "Garage", linksToHere: [{from: "Kitchen", distance: 4}], targets: [{text: "Garage door"}, {text: "Garage toolbox"}, {text: "Garage heater"}]},
	{name: "HallCloset", linksToHere: [{from: "Garage", distance: 2},{from: "Kitchen", distance: 4}], targets: [{text: "HallCloset"}]},
]
*/
export type Room = {
	name: string;
	linksToHere: Array<{from: string, distance: number}>;
	targets_raw: string[];
	targets_full: Target[];
};

export function ParseCustomPromptPool(customPoolStr: string|n) {
	const lines = (customPoolStr ?? "").split("\n").filter(a=>a != "" && !a.startsWith("#"));

	const rooms = [] as Room[];
	let lastRoomName: string|n = null;
	for (const line of lines) {
		const prefixPart_full = line.includes(":") ? line.split(":")[0] : null;

		// look for tags in the prefix-text (tag: text in square-brackets), and parse room-links from that
		let prefixPart_noTags = prefixPart_full ? prefixPart_full : null;
		const tags = [] as string[];
		const linksToHere: Array<{from: string, distance: number}> = [];
		if (prefixPart_full && prefixPart_noTags) {
			const tagRegex = /\[(.+?)\]/g;
			let match: RegExpExecArray | null;
			while ((match = tagRegex.exec(prefixPart_full)) != null) {
				tags.push(match[1]);
				prefixPart_noTags = prefixPart_noTags.replace(match[0], "");
			}

			// process link tags (format: "FromRoom->Distance" or "^->Distance")
			for (const tag of tags) {
				const linkMatch = tag.match(/^(.+?)->(\d+)$/);
				if (linkMatch) {
					const fromRoom = linkMatch[1] == "^" ? lastRoomName : linkMatch[1];
					const distance = parseFloat(linkMatch[2]);
					if (fromRoom) {
						linksToHere.push({from: fromRoom, distance});
					}
				}
				// todo: probably add "quality" tag-type (which is just a single number)
			}
		}

		// parse room name
		const roomNumber = rooms.length + 1; // first room is "Room1" (more user-friendly naming)
		const roomName = prefixPart_noTags ? prefixPart_noTags : `Room${roomNumber}`; // if no room name specified, generate one

		// parse targets in the room
		const entryEndersPart = prefixPart_full ? line.substring(prefixPart_full.length + 1).trim() : line.trim();
		const targets = [] as string[];
		if (entryEndersPart) {
			const entryEnders = entryEndersPart.split(",").map(a=>a.trim());
			for (const entryEnder of entryEnders) {
				targets.push(entryEnder);
			}
		} else {
			// room with no explicit targets gets an " " as its one target (a single-char space)
			targets.push(" ");
		}

		rooms.push({
			name: roomName,
			linksToHere,
			targets_raw: targets,
			targets_full: targets.map(a=>MakeTarget(`${roomName} ${a}`)),
		});

		lastRoomName = roomName;
	}

	// if only 1 room, and it's the auto-generated "Room1" name, change the "targets_full" to not include the room-name
	if (rooms.length == 1 && rooms[0].name == "Room1") {
		rooms[0].targets_full = rooms[0].targets_raw.map(a=>MakeTarget(a));
	}

	return rooms;
}